/**
 * VapiContext
 * ===========
 * Manages the Vapi AI voice agent with a formal state machine and two
 * operating modes:
 *
 * 1. AI Assistant mode  — agent starts a Vapi AI call (ASSISTANT_ID).
 *    State machine: Idle → Greeting → Listening → Processing → Speaking → Idle
 *
 * 2. Puppeteer mode     — agent is on a WebRTC call; Vapi transcribes the
 *    agent's speech in real-time.  `isTalking` is driven by Web Audio API
 *    RMS analysis (AnalyserNode) of the local mic stream, not Vapi events.
 *    The WebRTC audio track is NOT interrupted.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import Vapi from '@vapi-ai/web';

// ─── State Machine ────────────────────────────────────────────────────────────

/**
 * Formal state machine for the AI voice agent.
 * Matches Section 5.1 of 3_5_VOICE_VIDEO_CALLING_AI_IMPLEMENTATION_PLAN.md.
 */
export enum VoiceAgentState {
  IDLE              = 'IDLE',
  GREETING          = 'GREETING',
  LISTENING         = 'LISTENING',
  PROCESSING        = 'PROCESSING',
  SPEAKING          = 'SPEAKING',
  /** AI has announced transfer; system is finding an available agent. */
  WAITING_FOR_AGENT = 'WAITING_FOR_AGENT',
  /** Active handover in progress (three-way connection phase). */
  TRANSFERRING      = 'TRANSFERRING',
  /** Call has ended cleanly — transient before returning to IDLE. */
  ENDED             = 'ENDED',
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TranscriptEntry {
  role: 'user' | 'assistant';
  text: string;
}

/**
 * Context summary passed to the human agent on handover (FR-H003).
 * Mirrors the CONTEXT PASSED TO AGENT block in Section 5.4 of the plan.
 */
export interface HandoverContext {
  escalationReason: string;
  aiTurns:          number;
  lastCaption:      string;
  transcript:       TranscriptEntry[];
  /** ISO timestamp of when the handover was initiated. */
  initiatedAt:      string;
}

export type CallStatus = 'idle' | 'loading' | 'active';
export type WlWsState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'error';

interface VapiContextValue {
  /** RMS-driven talking flag (AI mode: Vapi events; Puppeteer mode: mic RMS). */
  isTalking: boolean;

  /** Current state of the AI voice agent state machine. */
  agentState: VoiceAgentState;

  // ── AI Assistant mode ──────────────────────────────────────────────────────
  callStatus: CallStatus;
  transcript: TranscriptEntry[];
  startCall: () => void;
  endCall:   () => void;

  // ── Human-AI handover (FR-H002 / FR-H003) ─────────────────────────────────
  /**
   * Initiate a graceful handover to a human agent.
   * Sets state → WAITING_FOR_AGENT, captures the handover context packet
   * (transcript, ai turns, last caption), then stops Vapi.
   */
  initiateHandover: (reason?: string) => HandoverContext;
  /** The context packet from the last handover, or null if no handover has occurred. */
  lastHandoverContext: HandoverContext | null;

  // ── Puppeteer mode ─────────────────────────────────────────────────────────
  puppeteerMode: boolean;
  /** Latest sentence spoken by the agent (shown as captions in the widget). */
  agentCaption: string;
  /**
   * Enable Puppeteer mode. Connects the provided MediaStream to a Web Audio
   * AnalyserNode; RMS > RMS_THRESHOLD → isTalking = true.  Opens a
   * WhisperLivekit WebSocket so the agent's speech is converted to real-time
   * captions (agentCaption).
   */
  enablePuppeteerMode: (stream: MediaStream) => Promise<void>;
  disablePuppeteerMode: () => void;
  /** WhisperLivekit WebSocket connection state (for reconnecting banner). */
  wlWsState: WlWsState;
  /**
   * Point the AnalyserNode at a MediaStream.  Called internally by
   * enablePuppeteerMode but exposed so callers can hot-swap streams.
   */
  syncMicToAnalyser: (stream: MediaStream) => void;

  // ── Agent avatar (FR-VD011) ───────────────────────────────────────────────
  /** Base64 data-URL of the agent's avatar photo, or null if not set. */
  agentAvatarUrl: string | null;
  /** Persist a new avatar URL to context + localStorage. Pass null to clear. */
  updateAgentAvatar: (url: string | null) => void;

  // ── Hub visibility ────────────────────────────────────────────────────────
  /** True when the user has explicitly opened the call hub (survives brief Vapi errors). */
  hubOpen: boolean;
  openHub:  () => void;
  closeHub: () => void;

  // ── Back-compat aliases ────────────────────────────────────────────────────
  /** @deprecated Use puppeteerMode */
  avatarMode: boolean;
  /** @deprecated Use enablePuppeteerMode */
  enableAvatarMode: (existingStream?: MediaStream) => Promise<void>;
  /** @deprecated Use disablePuppeteerMode */
  disableAvatarMode: () => void;
  /** @deprecated Use syncMicToAnalyser */
  syncAvatarToMic: (stream: MediaStream) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const VAPI_PUBLIC_KEY = '18216055-5685-40ec-bc13-5b44c95d9d0a';
const ASSISTANT_ID    = '29da479d-8613-45f7-86b3-4b29f16daf7d';
const AVATAR_API      = (process.env.REACT_APP_API_URL || 'http://localhost:8001/api/v1') + '/avatar/digital-twin';

/**
 * RMS amplitude threshold (0.0–1.0) above which the agent is considered to be
 * speaking.  0.02 works well in a quiet room; raise toward 0.05 in noisy
 * environments.
 */
const RMS_THRESHOLD = 0.02;

// ─── Context ──────────────────────────────────────────────────────────────────

const VapiContext = createContext<VapiContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export const VapiProvider = ({ children }: { children: React.ReactNode }) => {
  const vapiRef         = useRef<Vapi | null>(null);
  /** Ref mirror of puppeteerMode so event handlers see the current value
   *  without being re-registered on every mode change (avoids stale closures). */
  const puppeteerModeRef = useRef(false);

  // ── State machine ─────────────────────────────────────────────────────────
  const [agentState, setAgentState] = useState<VoiceAgentState>(VoiceAgentState.IDLE);

  // ── Shared state ─────────────────────────────────────────────────────────
  const [isTalking,  setIsTalking]  = useState(false);

  // ── Hub open flag — lets the toolbar open the hub without starting a call ─
  const [hubOpen, setHubOpen] = useState(false);

  // ── AI assistant state ────────────────────────────────────────────────────
  const [callStatus,  setCallStatus]  = useState<CallStatus>('idle');
  const [transcript,  setTranscript]  = useState<TranscriptEntry[]>([]);

  // ── Puppeteer mode state ──────────────────────────────────────────────────
  const [puppeteerMode,   setPuppeteerMode]   = useState(false);
  const [agentCaption,    setAgentCaption]    = useState('');

  // ── Agent avatar (FR-VD011) — persisted to localStorage ─────────────────
  const [agentAvatarUrl, setAgentAvatarUrl] = useState<string | null>(
    () => localStorage.getItem('converse_agent_avatar'),
  );

  const updateAgentAvatar = useCallback((url: string | null) => {
    setAgentAvatarUrl(url);
    if (url) localStorage.setItem('converse_agent_avatar', url);
    else localStorage.removeItem('converse_agent_avatar');
  }, []);

  // ── Web Audio refs (RMS / isTalking) ─────────────────────────────────────
  const audioCtxRef    = useRef<AudioContext | null>(null);
  const analyserRef    = useRef<AnalyserNode | null>(null);
  const rafRef         = useRef<number | null>(null);
  /** Stream we acquired via getUserMedia — we must stop its tracks on cleanup. */
  const ownedStreamRef = useRef<MediaStream | null>(null);

  // ── WhisperLivekit refs (puppeteer-mode transcription) ───────────────────
  const wlWsRef   = useRef<WebSocket | null>(null);
  const wlCtxRef  = useRef<AudioContext | null>(null);
  const wlProcRef = useRef<ScriptProcessorNode | null>(null);
  const [wlWsState, setWlWsState] = useState<WlWsState>('idle');

  // Keep ref mirror in sync
  useEffect(() => { puppeteerModeRef.current = puppeteerMode; }, [puppeteerMode]);

  // ── Vapi initialisation ───────────────────────────────────────────────────
  useEffect(() => {
    let vapi: Vapi;
    try {
      vapi = new Vapi(VAPI_PUBLIC_KEY);
    } catch (err) {
      console.error('[VapiContext] Failed to init Vapi SDK:', err);
      return;
    }
    vapiRef.current = vapi;

    // ── State machine transitions driven by Vapi events ───────────────────

    vapi.on('call-start', () => {
      setCallStatus('active');
      setAgentState(VoiceAgentState.GREETING);
    });

    vapi.on('call-end', () => {
      setCallStatus('idle');
      setAgentState(VoiceAgentState.IDLE);
      if (!puppeteerModeRef.current) setIsTalking(false);
    });

    // AI is speaking → SPEAKING; avatar driven by Vapi (not RMS) in AI mode.
    vapi.on('speech-start', () => {
      if (!puppeteerModeRef.current) {
        setIsTalking(true);
        setAgentState(VoiceAgentState.SPEAKING);
      }
    });

    // AI finished speaking → LISTENING (ready for user input).
    vapi.on('speech-end', () => {
      if (!puppeteerModeRef.current) {
        setIsTalking(false);
        setAgentState(VoiceAgentState.LISTENING);
      }
    });

    vapi.on('message', (msg: any) => {
      if (
        msg?.type === 'transcript' &&
        msg?.transcriptType === 'final' &&
        msg?.transcript
      ) {
        if (puppeteerModeRef.current) {
          // Puppeteer mode: agent's speech is on the "user" side.
          if (msg.role === 'user') {
            setAgentCaption(String(msg.transcript));
          }
          // Silently drop assistant transcripts (model is muted).
        } else {
          // AI mode: transition to PROCESSING while we receive the utterance,
          // then back to LISTENING after accumulating it.
          setAgentState(VoiceAgentState.PROCESSING);
          const role: 'user' | 'assistant' =
            msg.role === 'assistant' ? 'assistant' : 'user';
          setTranscript(prev => [...prev, { role, text: String(msg.transcript) }]);
          // Return to LISTENING after appending the transcript entry.
          setAgentState(VoiceAgentState.LISTENING);
        }
      }
    });

    vapi.on('error', (err: unknown) => {
      console.error('[VapiContext] error:', err);
      if (!puppeteerModeRef.current) {
        setCallStatus('idle');
        setAgentState(VoiceAgentState.IDLE);
        setIsTalking(false);
      }
    });

    return () => {
      vapi.stop();
      _stopMicAnalysis();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Web Audio: mic volume analysis ────────────────────────────────────────

  /** Tear down the AnalyserNode + AudioContext + animation frame. */
  const _stopMicAnalysis = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    // Only stop tracks we acquired ourselves, not the caller's WebRTC stream.
    if (ownedStreamRef.current) {
      ownedStreamRef.current.getTracks().forEach(t => t.stop());
      ownedStreamRef.current = null;
    }
    setIsTalking(false);
  }, []);

  /**
   * Connect a MediaStream to an AnalyserNode and start an rAF loop that
   * computes RMS amplitude; sets isTalking when it exceeds RMS_THRESHOLD.
   * This is the sole driver of lip-sync animation in Puppeteer mode.
   */
  const syncMicToAnalyser = useCallback((stream: MediaStream) => {
    // Tear down any previous analysis without stopping the stream itself.
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {});

    const audioCtx = new AudioContext();
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize              = 512;
    analyser.smoothingTimeConstant = 0.4;

    // Connect the stream.  The stream's audio still flows to the WebRTC
    // peer connection independently — we only read time-domain data here.
    const source = audioCtx.createMediaStreamSource(stream);
    source.connect(analyser);

    audioCtxRef.current  = audioCtx;
    analyserRef.current  = analyser;

    const buf = new Uint8Array(analyser.frequencyBinCount);

    const tick = () => {
      analyser.getByteTimeDomainData(buf);
      // Byte values are 0–255, centred at 128 (silence = flat line at 128).
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128; // normalise to [-1, 1]
        sum += v * v;
      }
      const rms = Math.sqrt(sum / buf.length);
      setIsTalking(rms > RMS_THRESHOLD);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
  }, []);

  // ── WhisperLivekit helpers ────────────────────────────────────────────────

  /** Tear down the WL WebSocket + AudioContext used for puppeteer transcription. */
  const _stopWlConnection = useCallback(() => {
    if (wlProcRef.current) {
      try { wlProcRef.current.disconnect(); } catch (_) {}
      wlProcRef.current = null;
    }
    if (wlCtxRef.current) {
      wlCtxRef.current.close().catch(() => {});
      wlCtxRef.current = null;
    }
    if (wlWsRef.current) {
      wlWsRef.current.close();
      wlWsRef.current = null;
    }
    setWlWsState('idle');
  }, []);

  /**
   * Open a WhisperLivekit WebSocket and pipe `stream` audio into it.
   * On disconnect while still in puppeteer mode, reconnects after 2 s.
   */
  const _openWlConnection = useCallback((stream: MediaStream) => {
    const wsUrl =
      (process.env.REACT_APP_WHISPER_WS_URL as string | undefined) ||
      'ws://localhost:9090';

    setWlWsState('connecting');

    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl);
    } catch (err) {
      console.error('[VapiContext] WL WebSocket init failed:', err);
      setWlWsState('error');
      return;
    }
    wlWsRef.current = ws;

    ws.onopen = () => {
      // Close any AudioContext left over from a previous connection attempt
      if (wlProcRef.current) {
        try { wlProcRef.current.disconnect(); } catch (_) {}
        wlProcRef.current = null;
      }
      if (wlCtxRef.current) {
        wlCtxRef.current.close().catch(() => {});
        wlCtxRef.current = null;
      }

      ws.send(JSON.stringify({
        uid:          `agent_${Date.now()}`,
        language:     'en',
        task:         'transcribe',
        model:        (process.env.REACT_APP_WHISPER_MODEL as string | undefined) || 'small.en',
        use_vad:      true,
        multilingual: false,
        backend:      'faster_whisper',
      }));

      // Set up raw PCM capture: float32 → int16 → WL binary frames
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ctx = new (window.AudioContext || (window as any).webkitAudioContext)({
          sampleRate: 16000,
        }) as AudioContext;
        wlCtxRef.current = ctx;

        const source = ctx.createMediaStreamSource(stream);
        const proc = ctx.createScriptProcessor(4096, 1, 1);
        wlProcRef.current = proc;

        proc.onaudioprocess = (e: AudioProcessingEvent) => {
          if (ws.readyState !== WebSocket.OPEN) return;
          const f32 = e.inputBuffer.getChannelData(0);
          const i16 = new Int16Array(f32.length);
          for (let i = 0; i < f32.length; i++) {
            i16[i] = Math.max(-32768, Math.min(32767, Math.round(f32[i] * 32767)));
          }
          ws.send(i16.buffer);
        };

        source.connect(proc);
        proc.connect(ctx.destination);
      } catch (audioErr) {
        console.error('[VapiContext] WL audio setup failed:', audioErr);
      }
    };

    ws.onmessage = (ev) => {
      try {
        const raw = typeof ev.data === 'string' ? ev.data : null;
        if (!raw) return;
        if (raw === 'SERVER_READY') { setWlWsState('connected'); return; }
        const data = JSON.parse(raw);
        if (data?.message === 'SERVER_READY') { setWlWsState('connected'); return; }
        const segs: Array<{ text?: string; completed?: boolean }> =
          data?.segments ?? [];
        const texts = segs
          .filter(s => s.completed !== false)
          .map(s => (s.text ?? '').trim())
          .filter(Boolean);
        if (texts.length > 0) {
          setAgentCaption(prev => {
            const next = prev ? `${prev} ${texts.join(' ')}` : texts.join(' ');
            return next.length > 400 ? next.slice(-400) : next;
          });
        }
      } catch (_) {}
    };

    ws.onclose = () => {
      wlWsRef.current = null;
      if (puppeteerModeRef.current) {
        setWlWsState('reconnecting');
        setTimeout(() => {
          if (puppeteerModeRef.current) _openWlConnection(stream);
        }, 2000);
      } else {
        setWlWsState('idle');
      }
    };

    ws.onerror = () => setWlWsState('error');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Puppeteer mode entry / exit ───────────────────────────────────────────

  /**
   * Enable Puppeteer mode.
   * - Connects `stream` to AnalyserNode for RMS → isTalking (lip-sync).
   * - Opens a WhisperLivekit WebSocket for real-time agent-speech captions.
   *   Replaces the previous Deepgram/Vapi transcription path.
   */
  const enablePuppeteerMode = useCallback(async (stream: MediaStream) => {
    setPuppeteerMode(true);
    puppeteerModeRef.current = true;
    setAgentCaption('');

    syncMicToAnalyser(stream);
    _openWlConnection(stream);
  }, [syncMicToAnalyser, _openWlConnection]);

  const disablePuppeteerMode = useCallback(() => {
    setPuppeteerMode(false);
    puppeteerModeRef.current = false;
    setAgentCaption('');
    _stopMicAnalysis();
    _stopWlConnection();
  }, [_stopMicAnalysis, _stopWlConnection]);

  // ── Back-compat aliases ───────────────────────────────────────────────────

  const enableAvatarMode = useCallback(async (existingStream?: MediaStream) => {
    if (!existingStream) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 16000, channelCount: 1 },
        });
        ownedStreamRef.current = stream;
        await enablePuppeteerMode(stream);
      } catch (err) {
        console.error('[VapiContext] getUserMedia failed:', err);
      }
    } else {
      await enablePuppeteerMode(existingStream);
    }
  }, [enablePuppeteerMode]);

  const avatarMode        = puppeteerMode;
  const disableAvatarMode = disablePuppeteerMode;
  const syncAvatarToMic   = syncMicToAnalyser;

  // ── Human-AI handover (FR-H002 / FR-H003) ────────────────────────────────

  const [lastHandoverContext, setLastHandoverContext] = useState<HandoverContext | null>(null);

  /**
   * Graceful handover to a human agent (Section 5.4 of the plan).
   * 1. Moves state → WAITING_FOR_AGENT (FR-H005 announcement phase).
   * 2. Captures the FR-H003 context packet (transcript, ai turns, caption).
   * 3. Stops the Vapi AI call.
   * Returns the context packet so the caller can display it to the agent.
   */
  const initiateHandover = useCallback((reason = 'Agent requested takeover'): HandoverContext => {
    setAgentState(VoiceAgentState.WAITING_FOR_AGENT);

    // Silence AI output immediately so the customer does not hear further speech
    try { (vapiRef.current as any)?.setMuted?.(true); } catch (_) {}

    const ctx: HandoverContext = {
      escalationReason: reason,
      aiTurns:          transcript.length,
      lastCaption:      agentCaption,
      transcript:       [...transcript],
      initiatedAt:      new Date().toISOString(),
    };
    setLastHandoverContext(ctx);

    // Brief delay so WAITING_FOR_AGENT renders, then fully stop Vapi
    setTimeout(() => {
      vapiRef.current?.stop();
      setAgentState(VoiceAgentState.IDLE);
    }, 800);

    return ctx;
  }, [transcript, agentCaption]);

  // ── AI assistant mode ─────────────────────────────────────────────────────

  const startCall = useCallback(() => {
    if (!vapiRef.current || callStatus !== 'idle') return;
    setCallStatus('loading');
    setTranscript([]);
    setAgentState(VoiceAgentState.GREETING);

    fetch(AVATAR_API)
      .then(r => r.ok ? r.json() : null)
      .catch(() => null)
      .then((profile: { vapi_voice_provider?: string; vapi_voice_id?: string } | null) => {
        const provider = profile?.vapi_voice_provider?.trim();
        const voiceId  = profile?.vapi_voice_id?.trim();
        const overrides = (provider && voiceId)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ? { voice: { provider, voiceId } } as any
          : undefined;
        vapiRef.current?.start(ASSISTANT_ID, overrides);
      });
  }, [callStatus]);

  const endCall = useCallback(() => {
    vapiRef.current?.stop();
    setAgentState(VoiceAgentState.IDLE);
  }, []);

  return (
    <VapiContext.Provider
      value={{
        isTalking,
        agentState,
        callStatus,
        transcript,
        startCall,
        endCall,
        puppeteerMode,
        agentCaption,
        enablePuppeteerMode,
        disablePuppeteerMode,
        syncMicToAnalyser,
        wlWsState,
        // Human-AI handover
        initiateHandover,
        lastHandoverContext,
        // Agent avatar
        agentAvatarUrl,
        updateAgentAvatar,
        // Hub visibility
        hubOpen,
        openHub:  () => setHubOpen(true),
        closeHub: () => setHubOpen(false),
        // Back-compat
        avatarMode,
        enableAvatarMode,
        disableAvatarMode,
        syncAvatarToMic,
      }}
    >
      {children}
    </VapiContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useVapi = (): VapiContextValue => {
  const ctx = useContext(VapiContext);
  if (!ctx) throw new Error('useVapi must be used inside <VapiProvider>');
  return ctx;
};
